// CFG.I18N.A1: i18next bootstrap for the BluePrint web app.
//
// Sprint scope: English only. The framework is wired up so a future sprint
// can add a new language without touching component code — drop a new
// translation file under `apps/web/src/locales/<lang>/translation.json` and
// add the locale to the `AVAILABLE_LOCALES` array below. Everything else
// (LanguageSwitcher visibility, fallback chain, namespace resolution) keys
// off that array.
//
// Why a single namespace ("translation") instead of per-feature namespaces:
// the wizards are the only thing internationalised right now, and the key
// inventory is small enough (~60 strings) that a single tree under
// `onboarding.{sme,individual}.*` is easier to scan than splitting by
// component. If a future sprint internationalises Settings / Dashboard /
// chat we can add namespaces (`settings`, `dashboard`, etc.) without
// breaking existing keys — react-i18next merges them transparently.

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import enTranslation from './locales/en/translation.json';

/**
 * Locales the app currently ships translations for. Adding a new locale =
 * add the file under `locales/<code>/translation.json`, import it above,
 * register it on this array AND in the `resources` map below. The
 * `LanguageSwitcher` component derives its dropdown contents from this
 * array, so it auto-hides while the array has length 1 (English only).
 */
export interface LocaleDescriptor {
  /** ISO 639-1 code passed to i18next (e.g., 'en', 'fr', 'es'). */
  code: string;
  /** Native name shown in the LanguageSwitcher dropdown ("English", "Français"). */
  label: string;
}

export const AVAILABLE_LOCALES: LocaleDescriptor[] = [
  { code: 'en', label: 'English' },
];

export const DEFAULT_LOCALE = 'en';

void i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: enTranslation },
    },
    lng: DEFAULT_LOCALE,
    fallbackLng: DEFAULT_LOCALE,
    // We control the entire translation surface — anything missing is a bug
    // in our own JSON, not a third-party fragment, so blank-strings are
    // worse than the key itself surfacing.
    returnEmptyString: false,
    interpolation: {
      // React already escapes output, so i18next double-escaping just
      // garbles ampersands etc.
      escapeValue: false,
    },
    // No browser language detection in this sprint — the wizards are
    // English-only and the language-detector adds noise to the bundle.
    // Add `i18next-browser-languagedetector` and `.use(LanguageDetector)`
    // when introducing the second locale.
  });

export default i18n;
